import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans } from '@lingui/react';
import * as React from 'react';
import { Transactions as TransactionList } from './Transactions';
import { stripeProducts } from '../../../../environments/environment';
import { useTransactionsViewSuspenseQuery } from './transactions-view.graphql';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { Switch } from '@startuptools/ui/Switch';
import { Chip } from '@startuptools/ui/Chip';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { isNil } from 'lodash-es';
import {
  FREE_SHAREHOLDERS_LIMIT,
  STARTER_SHAREHOLDERS_LIMIT,
  UpgradeBanner,
} from '@startuptools/business-ui/UpgradeBanner';
import { NextGroupDialog } from './TransactionGroup/NextGroup';
import { useCurrentUserSuspenseQuery } from '../../../graphql/react-operations';
import { useNavigate } from '../../../components/Navigate';
import { Route, Routes } from 'react-router-dom';
import { TransactionGroup } from './TransactionGroup';

const TransactionsInner = () => {
  const companyId = useCompanyId();
  const {
    data: {
      shCount,
      shareTransactionGroups,
      companySubscription,
      nextShareCapitalChanges: { next },
      company: { shareRegisterLimit },
    },
  } = useTransactionsViewSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });
  const { navigate } = useNavigate();

  const {
    data: { currentUser: user },
  } = useCurrentUserSuspenseQuery();
  const [showTransactionGroupDialog, setShowTransactionGroupDialog] = React.useState(false);
  const [showShareRanges, setShowShareRanges] = React.useState(false);

  const showFreeUpgradeBanner =
    isNil(companySubscription) && shCount.count >= (shareRegisterLimit ?? FREE_SHAREHOLDERS_LIMIT);

  const showStarterUpgradeBanner =
    !isNil(companySubscription) &&
    companySubscription.mainProduct === stripeProducts.startup &&
    shCount.count >= (shareRegisterLimit ?? STARTER_SHAREHOLDERS_LIMIT);

  // We do not yet have the means to differentiate between old and new ones, so let's not enforce it until we can do that.
  // const showGrowUpgradeBanner =
  //   !isNil(companySubscription) &&
  //   companySubscription.mainProduct === stripeProducts.startup &&
  //   shCount.count >= GROW_SHAREHOLDERS_LIMIT;

  const showUpgradeBanner = showFreeUpgradeBanner || showStarterUpgradeBanner; // || showGrowUpgradeBanner;

  return (
    <Stack gap={2}>
      {showUpgradeBanner && (
        <UpgradeBanner
          feature="share_register"
          navigateToBilling={() => navigate(`/settings/billing`)}
          customShareRegisterLimit={shareRegisterLimit}
        />
      )}
      <Stack direction="column" spacing={2}>
        {next && (
          <Alert color="success">
            {shareTransactionGroups.length ? (
              <Trans id="There's one or more cases from Bolagsverket waiting to be registered. Click the button below to get started." />
            ) : (
              <Trans id="This is where you add the share register for the company, starting with the company creation." />
            )}
          </Alert>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button onClick={() => setShowTransactionGroupDialog(true)}>
            {shareTransactionGroups.length ? <Trans id="Register event" /> : <Trans id="Get started" />}
          </Button>

          {user.admin && (
            <Switch
              checked={showShareRanges}
              onChange={() => setShowShareRanges(!showShareRanges)}
              endDecorator={
                <Stack direction="row" gap={1}>
                  <Trans id="Show Share Ranges" />{' '}
                  <Tooltip title={<Trans id="Sys-admin feature only" />}>
                    <Chip size="sm" color="neutral" variant="outlined">
                      admin
                    </Chip>
                  </Tooltip>
                </Stack>
              }
            />
          )}
        </Stack>
      </Stack>

      <TransactionList
        navigateToStg={id => navigate(`/share-register/transactions/${id}`)}
        showNewTransactionButton
        showShareRanges={showShareRanges}
      />
      {showTransactionGroupDialog && (
        <NextGroupDialog skipPrompt onClose={() => setShowTransactionGroupDialog(false)} />
      )}
    </Stack>
  );
};

const Transactions = () => {
  return (
    <Stack gap={2}>
      <Typography level="h2">
        <Trans id="Transactions" />
      </Typography>

      <React.Suspense
        fallback={
          <Stack gap={2} mt={2}>
            {[0, 1, 2].map(n => (
              <Skeleton key={n} variant="rectangular" width="100%" height="2em" />
            ))}
          </Stack>
        }
      >
        <TransactionsInner />
      </React.Suspense>
    </Stack>
  );
};

export const TransactionsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Transactions />} />
      <Route path=":shareTransactionGroupId" element={<TransactionGroup />} />
    </Routes>
  );
};
